import { type Account, type ListAccountsResponse } from '@qdrant/qdrant-cloud-public-api/account/v1';
import { listAccounts } from '@qdrant/qdrant-cloud-public-api/account/v1/connect-query';
import { type GetAuthenticatedUserResponse } from '@qdrant/qdrant-cloud-public-api/iam/v1';
import { getAuthenticatedUser } from '@qdrant/qdrant-cloud-public-api/iam/v1/connect-query';
import { type QueryClient } from '@tanstack/react-query';
import { createConnectQueryKey } from '@/connect-query';
import { sortedUserAccounts } from '@/routes/_authenticated.hooks';

/**
 * Resolves to a fallback account for the user after leaving or removing an account.
 */
export const resolveFallbackAccount = async ({
  queryClient,
  accountId,
}: {
  queryClient: QueryClient;
  accountId: string;
}): Promise<Account | void> => {
  const authenticatedUserQueryKey = createConnectQueryKey({ schema: getAuthenticatedUser });
  const accountsQueryKey = createConnectQueryKey({ schema: listAccounts });
  // Retrieve fresh authenticated user and accounts from the cache
  const { user } = await queryClient.fetchQuery<GetAuthenticatedUserResponse>({
    queryKey: authenticatedUserQueryKey,
    staleTime: 'static',
  });
  // Retrieve a fresh list of accounts from the cache
  const { items: accounts } = await queryClient.fetchQuery<ListAccountsResponse>({
    queryKey: accountsQueryKey,
    staleTime: 'static',
  });
  // Check if we have an authenticated user and a list of accounts
  if (user && accounts) {
    const remainingAccounts = accounts.filter((account) => account.id !== accountId);
    // If there are remaining accounts, find a suitable fallback account to redirect the user to
    if (remainingAccounts.length > 0) {
      return sortedUserAccounts(remainingAccounts, user)[0];
    }
  }
};
